import { CheckCircle2 } from "lucide-react";
import { focusRing } from "../ui/focusRing";

export type BillingCycle = "monthly" | "annual";

interface PricingSectionProps {
  billingCycle: BillingCycle;
  setBillingCycle: (cycle: BillingCycle) => void;
  slackOauthUrl: string;
  slackOauthUrlTrial: string;
}

type BillingToggleProps = {
  billingCycle: BillingCycle;
  setBillingCycle: (cycle: BillingCycle) => void;
  size?: "sm" | "md";
  tone?: "light" | "dark";
  discountStyle?: "full" | "short" | "none";
};

function BillingToggle({
  billingCycle,
  setBillingCycle,
  size = "md",
  tone = "light",
  discountStyle = "full",
}: BillingToggleProps) {
  const buttonClass = size === "md" ? "px-4 py-2 text-sm" : "px-3 py-1.5 text-xs";
  const containerSizeClass = size === "md" ? "min-w-[240px]" : "min-w-[200px]";

  const trackClass =
    tone === "dark" ? "bg-white/15 border-white/25" : "bg-stone-100 border-stone-200";

  const thumbClass = tone === "dark" ? "bg-white shadow-sm" : "bg-white shadow-sm";

  const selectedTextClass = tone === "dark" ? "text-stone-900" : "text-stone-900";
  const unselectedTextClass =
    tone === "dark" ? "text-white" : "text-stone-600 hover:text-stone-800";

  return (
    <div
      className={`relative inline-grid grid-cols-2 rounded-full border p-1 ${containerSizeClass} ${trackClass}`}
      role="tablist"
      aria-label="Billing cycle"
    >
      <div
        className={`absolute top-1 bottom-1 left-1 w-[calc(50%-0.25rem)] rounded-full transition-transform duration-200 ease-out ${thumbClass}`}
        style={{ transform: billingCycle === "annual" ? "translateX(100%)" : "translateX(0%)" }}
        aria-hidden="true"
      />
      <button
        type="button"
        role="tab"
        onClick={() => setBillingCycle("monthly")}
        className={`${buttonClass} relative z-10 rounded-full font-semibold transition-colors ${focusRing} ${
          billingCycle === "monthly" ? selectedTextClass : unselectedTextClass
        }`}
        aria-selected={billingCycle === "monthly"}
      >
        Monthly
      </button>
      <button
        type="button"
        role="tab"
        onClick={() => setBillingCycle("annual")}
        className={`${buttonClass} relative z-10 rounded-full font-semibold transition-colors ${focusRing} ${
          billingCycle === "annual" ? selectedTextClass : unselectedTextClass
        }`}
        aria-selected={billingCycle === "annual"}
      >
        Yearly
        {discountStyle === "full" && (
          <span className="ml-2 text-xs font-bold opacity-80">Save 17%</span>
        )}
        {discountStyle === "short" && (
          <span className="ml-1 text-xs font-bold opacity-80">−17%</span>
        )}
      </button>
    </div>
  );
}

export function PricingSection({
  billingCycle,
  setBillingCycle,
  slackOauthUrl,
  slackOauthUrlTrial,
}: PricingSectionProps) {
  return (
    <section id="pricing" className="py-20 px-6 bg-white scroll-mt-24">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
            Simple pricing, no surprises
          </h2>
          <p className="text-lg text-stone-600">Start free, upgrade when you're ready.</p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-orange-200 flex flex-col h-full">
              <div className="text-center mb-6">
                <span className="text-sm font-medium text-stone-500 uppercase tracking-wide">
                  Free
                </span>
                <p className="text-4xl font-bold text-stone-800 mt-2">$0</p>
                <p className="text-stone-500 mt-1">No credit card</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {["1 queue", "Up to 10 items", "All basic commands"].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-stone-700">
                    <CheckCircle2 className="w-5 h-5 text-brand-orange flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href={slackOauthUrl}
                className={`block w-full text-center bg-stone-100 hover:bg-stone-200 text-stone-800 py-3 rounded-full font-semibold transition-colors ${focusRing}`}
              >
                Add to Slack
              </a>
            </div>

            <div className="bg-gradient-to-r from-brand-orange to-brand-orange-hover text-white rounded-3xl p-8 shadow-xl relative overflow-hidden flex flex-col h-full">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                  <span className="text-sm font-medium text-white uppercase tracking-wide">
                    Pro
                  </span>
                </div>
                <div className="text-center">
                  <BillingToggle
                    billingCycle={billingCycle}
                    setBillingCycle={setBillingCycle}
                    size="sm"
                    tone="dark"
                    discountStyle="none"
                  />
                  <p className="text-xs text-white font-semibold mt-1">Save 17% with yearly</p>
                </div>
              </div>

              <div className="text-center mb-6">
                <p className="text-4xl font-bold mt-2">
                  {billingCycle === "monthly" ? (
                    <>
                      $10<span className="text-lg font-normal text-white">/mo</span>
                    </>
                  ) : (
                    <span className="inline-flex items-baseline gap-2">
                      <span className="font-semibold text-white/90 line-through">$120</span>
                      <span>
                        $100<span className="text-lg font-normal text-white">/yr</span>
                      </span>
                    </span>
                  )}
                </p>
                <p className="text-white mt-1">Per workspace</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  "Up to 5 active queues",
                  "Up to 50 items per queue",
                  "Due dates & stats",
                  "CSV export",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href={`${slackOauthUrlTrial}&plan=${billingCycle}`}
                className={`block w-full text-center bg-white text-stone-900 hover:bg-orange-50 py-3 rounded-full font-semibold transition-colors shadow-lg ${focusRing}`}
              >
                Try free for 30 days
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
